export { cn } from "@brev-io/common/src/utils/cn";

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type AnyFunction = (...args: unknown[]) => void;
export function debounce<T extends AnyFunction>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/**
 * Detect Safari and all iOS browsers (which use WebKit and share
 * Safari's third-party cookie blocking via ITP).
 */
export function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // All iOS/iPadOS browsers are required to use WebKit and enforce ITP.
  // This intentionally includes Chrome/Firefox on iOS (CriOS, FxiOS) since
  // they share the same third-party cookie restrictions as Safari.
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // macOS Safari (exclude Chrome, Edge, Opera, Brave)
  return (
    /safari/i.test(ua) &&
    !/chrome|chromium|edg|opera|brave/i.test(ua)
  );
}

/** Canonical money formatter (BRE-6018): 0–2 fraction digits, so whole
 *  amounts render without a forced ".00" ($5, $5.25). `currency` accepts any
 *  ISO 4217 code (case-insensitive — Intl canonicalizes) and defaults to USD;
 *  it exists for surfaces that render provider-reported currencies. */
export const formatCurrency = (value: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
};

export function formatNumber(number: number) {
  if (number >= 1000000000) {
    return (number / 1000000000).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    }) + 'B';
  } else if (number >= 1000000) {
    return (number / 1000000).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    }) + 'M';
  } else if (number >= 1000) {
    return (number / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    }) + 'K';
  } else {
    return number.toLocaleString();
  }
}

export function getErrorTitle(status: number | string): string {
  switch (status) {
    case 400:
      return "400 - Bad Request";
    case 401:
      return "401 - Unauthorized";
    case 403:
      return "403 - Forbidden";
    case 404:
      return "Page not found";
    case 500:
      return "500 - Internal Server Error";
    case 502:
      return "502 - Bad Gateway";
    case 503:
      return "503 - Service Unavailable";
    case 504:
      return "504 - Gateway Timeout";
    default:
      return `${status}`;
  }
}

export const capitalizeFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const cleanHtmlAndMarkdown = (text: string): string => {
  if (!text) return "";

  // Remove HTML tags (including inline styles)
  let cleaned = text.replace(/<[^>]*>/g, "");

  // Convert markdown formatting to plain text equivalents
  cleaned = cleaned
    // Bold text: **text** or __text__ -> text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")

    // Italic text: *text* or _text_ -> text
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")

    // Code blocks: ```code``` -> code
    .replace(/```[\s\S]*?```/g, (match) => {
      return match.replace(/```/g, "").trim();
    })

    // Inline code: `code` -> code
    .replace(/`([^`]+)`/g, "$1")

    // Links: [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")

    // Headers: ### Header -> Header
    .replace(/^#+\s*/gm, "")

    // List items: - item or * item -> • item. Keep the leading
    // indentation so nested lists stay nested when pasted as plain
    // text (BRE-4840 — the old `^[\s]*` swallowed it and flattened
    // every level to a top-level bullet).
    .replace(/^([ \t]*)[-*+]\s+/gm, "$1• ")

    // Remove extra whitespace and normalize line breaks
    .replace(/\n\s*\n\s*\n/g, "\n\n") // Multiple line breaks -> double line break
    // Collapse runs of spaces/tabs only after a non-space character —
    // anchoring on `\S` leaves line-leading indentation (nested list
    // levels) intact while still normalizing interior whitespace.
    .replace(/(\S)[ \t]+/g, "$1 ")
    .trim();

  return cleaned;
};


export interface TextFieldValidationOptions {
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  patternError?: string;
  required?: boolean;
}

const DEFAULT_TEXT_PATTERN = /^[\p{L}\p{N}\s&'.,/()!@#+-]+$/u;

export function validateTextField(
  value: string,
  fieldLabel: string,
  options: TextFieldValidationOptions = {}
): string | null {
  const {
    minLength = 1,
    maxLength = 255,
    pattern = DEFAULT_TEXT_PATTERN,
    patternError = "Only letters, numbers, spaces, hyphens, and common punctuation allowed",
    required = true
  } = options;

  const trimmed = value.trim();

  if (required && !trimmed) {
    return `${fieldLabel} is required`;
  }

  if (!required && !trimmed) {
    return null;
  }

  if (trimmed.length < minLength) {
    return `Must be at least ${minLength} characters`;
  }

  if (trimmed.length > maxLength) {
    return `Must be ${maxLength} characters or less`;
  }

  if (!pattern.test(trimmed)) {
    return patternError;
  }

  return null;
}

export const extractSummaryPlainText = (
  aiSummary?: {
    content?: string;
    summary?: {
      highlights?: string;
      improvements?: string;
      risks?: string;
    };
  },
  viewMeta?: { name?: string }
) => {
  const title = viewMeta?.name ? `${viewMeta.name} - AI Summary\n\n` : "AI Summary\n\n";

  // Check if this is the new format with unified content
  if (aiSummary?.content && aiSummary.content.trim().length > 0) {
    // New format: return title + cleaned markdown content
    return title + cleanHtmlAndMarkdown(aiSummary.content);
  }

  // Legacy format: construct from individual sections
  if (!aiSummary?.summary) return "";

  let content = title;

  if (aiSummary.summary.highlights) {
    content += "HIGHLIGHTS\n\n";
    content += cleanHtmlAndMarkdown(aiSummary.summary.highlights) + "\n\n";
  }

  if (aiSummary.summary.improvements) {
    content += "AREAS FOR IMPROVEMENT\n\n";
    content += cleanHtmlAndMarkdown(aiSummary.summary.improvements) + "\n\n";
  }

  if (aiSummary.summary.risks) {
    content += "POTENTIAL RISKS\n\n";
    content += cleanHtmlAndMarkdown(aiSummary.summary.risks) + "\n\n";
  }

  return content.trim();
};

/** Returns true when Cmd/Ctrl or Shift is held during a mouse event. */
export function hasModifierKey(e: Pick<MouseEvent, "metaKey" | "ctrlKey" | "shiftKey">): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey;
}

// Re-export date formatting utilities from consolidated module
export { formatRelativeTime } from "./dateFormatters";